import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded"
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded"
import { Avatar, useTheme, CircularProgress, Typography } from "@mui/material"
import { useConfirm } from "material-ui-confirm"
import { ReactElement } from "react"
import { KeyedMutator } from "swr"

import MfaChip from "./mfaChip"
import {
    TT_APPROVE_USER_BUTTON,
    TT_FALSE_MFA,
    TT_REJECT_USER_BUTTON,
} from "../../../constants/tooltips"
import useAddGroup from "../../../services/middleware/hooks/adminPortal/useAddGroup"
import useDeleteUser from "../../../services/middleware/hooks/adminPortal/useDeleteUser"
import {
    TUserOutput,
    TUsersOutput,
} from "../../../services/middleware/mappers/adminPortal/activeUsersMapper"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppTooltip from "../../helper/appTooltip"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IPendingUserRowProps {
    user: TUserOutput
    mutate: KeyedMutator<TUsersOutput>
}

/**
 * Displaying each single user row
 * @param pendingUsers - gets all users
 * @param selectedUserIds - selected users parameters
 * @param setSelectedUserIds - setter function for selecting users
 * @returns - displays each individual user that has an approved state of false within a table row
 */
const PendingUserRow = ({
    user: pendingUsers,
    mutate,
}: IPendingUserRowProps): ReactElement => {
    const theme = useTheme()
    const { addGroup, isLoading: addUserLoading } = useAddGroup(
        pendingUsers.userName,
        pendingUsers.fullName,
        mutate
    )
    const confirm = useConfirm()
    const falseMfa = pendingUsers.mfa === false
    const tableCellSx = { opacity: falseMfa ? 0.5 : 1 }
    const { deleteUser, isLoading: deleteUserLoading } = useDeleteUser(
        pendingUsers.userName,
        pendingUsers.fullName,
        mutate
    )

    const deleteConformation = (): void => {
        confirm({
            description: `Warning ${pendingUsers.fullName} will be permanently deleted and removed!`,
        })
            .then(() => {
                deleteUser()
            })
            .catch(() => {
                /* ... */
            })
    }

    const isRowLoading = (): boolean => addUserLoading || deleteUserLoading
    const isRowDisabled = (): boolean => isRowLoading() || falseMfa

    return (
        <AppTooltip title={pendingUsers.mfa === true ? "" : TT_FALSE_MFA}>
            <AppTableContentRow>
                <AppTableCell>
                    {isRowLoading() ? (
                        <CircularProgress />
                    ) : (
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.secondary.main,
                            }}
                        >
                            {pendingUsers?.initials}
                        </Avatar>
                    )}
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    <Typography color="text.primary">
                        {pendingUsers.fullName}
                    </Typography>
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    <Typography color="text.primary">
                        {pendingUsers.email}
                    </Typography>
                </AppTableCell>
                <AppTableCell sx={tableCellSx} align="center">
                    <MfaChip isFalseMfa={falseMfa} />
                </AppTableCell>
                <AppTableCell align="center">
                    <AppTooltip title={TT_APPROVE_USER_BUTTON}>
                        <AppButton
                            variant="contained"
                            color="success"
                            onClick={() => {
                                addGroup(
                                    `${pendingUsers.type}-APPROVED`,
                                    undefined,
                                    true
                                )
                            }}
                            disabled={isRowDisabled()}
                            sx={{ marginRight: GRID_SPACING }}
                            endIcon={<ThumbUpAltRoundedIcon />}
                        >
                            Approve
                        </AppButton>
                    </AppTooltip>
                    <AppTooltip title={TT_REJECT_USER_BUTTON}>
                        <AppButton
                            variant="contained"
                            color="error"
                            onClick={deleteConformation}
                            endIcon={<ThumbDownAltRoundedIcon />}
                            disabled={isRowLoading()}
                        >
                            Reject
                        </AppButton>
                    </AppTooltip>
                </AppTableCell>
            </AppTableContentRow>
        </AppTooltip>
    )
}

export default PendingUserRow
